import { useEffect, type RefObject } from "react";

/**
 * Calls `onDismiss` on Escape or on a pointer press outside every element in `refs` while
 * `isActive`. Pass the toggle button's container too, or pressing it would close and reopen.
 */
export function useDismiss(
  refs: ReadonlyArray<RefObject<HTMLElement | null>>,
  isActive: boolean,
  onDismiss: () => void,
): void {
  useEffect(() => {
    if (!isActive) return;

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (refs.every((ref) => !ref.current?.contains(target))) onDismiss();
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onDismiss();
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [refs, isActive, onDismiss]);
}
